import axios from "axios";
import { appConfig } from "@/config";
import { normalizeHttpError } from "./httpError";
import { API_TIMEOUT_MS, installRetry } from "./resilience";
import { installSimulatedLatency } from "./simulatedLatency";

/**
 * 인증 서버(overlord) 전용 HTTP client.
 *
 * Owns:
 * - 인증 서버 base URL(`appConfig.authBaseUrl`)과 주소 미설정 차단.
 * - timeout · 재시도 · 개발 지연 주입 설치(registry client 와 같은 정책 모듈을 쓴다).
 *
 * Does not own:
 * - Bearer 주입. 로그인 start/login 은 토큰 없이 호출한다. 토큰이 필요한 곳은 OTP 발급
 *   하나뿐이다. 그래서 호출부가 헤더를 명시한다.
 * - 401 의 해석. registry 의 401 은 세션 만료다. 여기서 401 은 state · code · OTP 가 무효라는
 *   뜻이다. 그래서 이 client 는 세션을 끊지 않는다(`apiClient` 의 무효화 인터셉터가 없다).
 */
export class AuthServerNotConfiguredError extends Error {
  constructor() {
    super("인증 서버 주소가 설정되지 않았습니다. VITE_AUTH_BASE_URL 을 설정해 주세요.");
    this.name = "AuthServerNotConfiguredError";
  }
}

export const authClient = axios.create({
  baseURL: appConfig.authBaseUrl,
  timeout: API_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
  },
});

installSimulatedLatency(authClient);
installRetry(authClient);

// 주소가 비어 있으면 요청이 same-origin 으로 가서 registry 에 없는 경로를 호출한다. 그러면 404
// 만 남고 원인이 보이지 않는다. 그래서 요청을 보내기 전에 중단하고 사유를 알린다.
authClient.interceptors.request.use((config) => {
  if (appConfig.authBaseUrl === "") {
    throw new AuthServerNotConfiguredError();
  }
  return config;
});

// 주소 미설정은 HTTP 오류가 아니라 설정 결함이다. 그래서 envelope으로 바꾸지 않고 그대로 던진다.
authClient.interceptors.response.use(
  (response) => response,
  (error: unknown) =>
    Promise.reject(
      error instanceof AuthServerNotConfiguredError ? error : normalizeHttpError(error),
    ),
);
